"use client"

import styled from "@emotion/styled"
import { Skeleton } from "@mui/material"
import { useLocale, useTranslations } from "next-intl"
import Script from "next/script"
import React from "react"
import { useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import { widgetLocaleConfig } from "utils"
import { FormObject } from "./Hero"

const ORIGIN = process.env.ORIGIN
const ENV = process.env.ENV

// Function to generate script content based on locale
const generateScript = (locale: string) => {
  const config =
    widgetLocaleConfig[locale as keyof typeof widgetLocaleConfig] ||
    widgetLocaleConfig.he

  return `
    (function(d, s, id) {
        var js, fjs = d.getElementsByTagName(s)[0];
        var ro = !!d.getElementById(id);
        js = d.createElement(s); js.id = id;
        js.src = "${config.scriptSrc}";
        fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'moovit-jsw');
  `
}
const desktopHeight = 320
const mobileHeight = 320

const StyledWidgetWrapper = styled.div`
  height: ${mobileHeight}px;
  overflow: hidden;
  .mv-gd-widget-20 {
    > div {
      border-radius: 0;
      border: none;
      h2 {
        display: none;
      }
      iframe {
        height: 100%;
        title: attr(data-iframe-title);
      }
    }
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: ${desktopHeight}px;
  }
`

type Props = {
  formObject: FormObject
}

const MoovitWidget: React.FC<Props> = () => {
  const locale = useLocale() as keyof typeof widgetLocaleConfig
  const t = useTranslations("Homepage")
  const config = widgetLocaleConfig[locale] || widgetLocaleConfig.he
  const isDesktop = useIsDesktop()
  const [loaded, setLoaded] = React.useState(false)

  const script = generateScript(locale)
  const redirectUrl =
    ENV === "development"
      ? `https://dev.egged.co.il/${locale}/journey-planner`
      : `${ORIGIN}/${locale}/journey-planner`

  React.useEffect(() => {
    if (loaded) {
      // Add title to iframe after it loads
      const iframe = document.querySelector(".mv-gd-widget-20 iframe")
      if (iframe) {
        iframe.setAttribute("title", t("tripSearchWidget"))
      }
    }
  }, [loaded, t])

  return (
    <StyledWidgetWrapper>
      <Script
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: script }}
        onReady={() => {
          setLoaded(true)
        }}
      />
      <div
        className="mv-gd-widget-20"
        data-width="100%"
        data-height={isDesktop ? desktopHeight : mobileHeight}
        data-id={config.dataId}
        data-partner="G8MC_Ex8IN0zXQ2QK2Xfsg"
        data-url={redirectUrl}
        data-target="parent"
        data-title={config.dataTitle}
      ></div>
      {!loaded && (
        <>
          <Skeleton
            variant="rectangular"
            height={55}
            width="100%"
            sx={{
              bgcolor: sharedColors.brandMain,
              opacity: 0.5,
              marginBottom: "8px",
            }}
          />
          <Skeleton
            variant="rectangular"
            height={55}
            width="100%"
            sx={{
              bgcolor: sharedColors.brandMain,
              opacity: 0.5,
              marginBottom: "8px",
            }}
          />
          <Skeleton
            variant="rectangular"
            height={55}
            width="100%"
            sx={{
              bgcolor: sharedColors.brandMain,
              opacity: 0.5,
              marginBottom: "8px",
            }}
          />
        </>
      )}
    </StyledWidgetWrapper>
  )
}

export default MoovitWidget
